/**
 * Radial Denomination Menu
 *
 * Circular menu for selecting bill denominations.
 * - Appears on long press
 * - Drag toward a segment to highlight it
 * - Release to select
 * - Drag back to center to cancel
 */

import { EventEmitter } from '../../engine/core/EventEmitter';
import { formatDenomination, getDenominations } from '../../game/betting/BillDenominations';

export interface RadialMenuEvents {
  'select': { denomination: number; index: number };
  'cancel': undefined;
  'close': undefined;
}

export interface RadialMenuConfig {
  container: HTMLElement;
  denominations?: number[];
  radius?: number;
  centerDeadzone?: number;
}

// Uses server denominations if loaded, otherwise fallback
function getDefaultDenominations(): number[] {
  const serverDenoms = getDenominations();
  if (serverDenoms.length > 0 && serverDenoms.length <= 12) {
    return serverDenoms;
  }
  // If too many server denoms for a radial menu, pick a sensible subset
  if (serverDenoms.length > 12) {
    // Take evenly spaced entries
    const step = Math.ceil(serverDenoms.length / 8);
    return serverDenoms.filter((_, i) => i % step === 0).slice(0, 8);
  }
  return [100, 500, 1000, 5000, 10000, 50000, 100000, 1000000];
}

const DEFAULT_DENOMINATIONS = getDefaultDenominations();

export class RadialDenominationMenu extends EventEmitter<RadialMenuEvents> {
  private container: HTMLElement;
  private root: HTMLElement;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;

  private denominations: number[];
  private radius: number;
  private centerDeadzone: number;

  private isVisible = false;
  private centerX = 0;
  private centerY = 0;
  private highlightedIndex = -1;
  private currentDenominationIndex = 0;

  // Gold-on-charcoal theme: single gold accent, fully opaque segments
  private readonly colors = {
    background: 'rgba(8, 8, 10, 1)',
    segment: 'rgba(21, 21, 26, 1)',            // #15151A charcoal, fully opaque
    segmentHighlight: '#FFD700',                // Solid gold fill when hovered
    segmentSelected: 'rgba(21, 21, 26, 1)',     // Charcoal fill; gold border marks current
    border: 'rgba(184, 150, 15, 0.35)',         // Gold-dim resting border
    borderHighlight: '#FFD700',                 // Bright gold border when hovered
    borderSelected: 'rgba(184, 150, 15, 0.9)',  // Brighter gold ring for current denom
    text: '#FFFFFF',
    textHighlight: '#08080A',                   // Dark text on gold fill
    textSelected: '#FFD700',                    // Gold text for current denom
    center: 'rgba(255, 215, 0, 0.18)',
    centerBorder: '#FFD700',
  };

  constructor(config: RadialMenuConfig) {
    super();

    this.container = config.container;
    this.denominations = config.denominations ?? DEFAULT_DENOMINATIONS;
    this.radius = config.radius ?? 150;
    this.centerDeadzone = config.centerDeadzone ?? 40;

    this.root = this.createElement();
    this.canvas = this.root.querySelector('canvas')!;
    this.ctx = this.canvas.getContext('2d')!;

    this.setupCanvas();
    this.injectStyles();
  }

  private createElement(): HTMLElement {
    const root = document.createElement('div');
    root.className = 'radial-menu';
    root.innerHTML = `
      <canvas class="radial-menu-canvas"></canvas>
      <div class="radial-menu-center">
        <span class="radial-menu-cancel">✕</span>
      </div>
    `;

    this.container.appendChild(root);
    return root;
  }

  private setupCanvas(): void {
    // High DPI support
    const dpr = window.devicePixelRatio || 1;
    const size = this.radius * 2 + 60; // Extra padding for labels

    this.canvas.width = size * dpr;
    this.canvas.height = size * dpr;
    this.canvas.style.width = `${size}px`;
    this.canvas.style.height = `${size}px`;

    this.ctx.scale(dpr, dpr);
  }

  private injectStyles(): void {
    if (document.getElementById('radial-menu-styles')) return;

    const styles = document.createElement('style');
    styles.id = 'radial-menu-styles';
    styles.textContent = `
      .radial-menu {
        position: fixed;
        pointer-events: none;
        z-index: 1000;
        opacity: 0;
        transform: scale(0.8);
        transition: opacity 0.15s ease, transform 0.15s ease;
      }

      .radial-menu.visible {
        opacity: 1;
        transform: scale(1);
        pointer-events: auto;
      }

      .radial-menu-canvas {
        display: block;
      }

      .radial-menu-center {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 64px;
        height: 64px;
        border-radius: 50%;
        background: rgba(21, 21, 26, 1);
        border: 2px solid rgba(184, 150, 15, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.15s ease;
      }

      .radial-menu-center.hover {
        background: rgba(255, 215, 0, 0.18);
        border-color: #FFD700;
      }

      .radial-menu-cancel {
        font-size: 26px;
        color: #FFD700;
        transition: opacity 0.15s ease;
      }

      .radial-menu-center.hover .radial-menu-cancel {
        opacity: 1;
      }
    `;
    document.head.appendChild(styles);
  }

  /**
   * Show the radial menu at a specific screen position
   */
  show(screenX: number, screenY: number, currentIndex: number = 0): void {
    this.isVisible = true;
    this.currentDenominationIndex = currentIndex;
    this.highlightedIndex = -1;

    // Position menu centered on touch point
    const size = this.radius * 2 + 60;
    this.centerX = size / 2;
    this.centerY = size / 2;

    // Clamp position to keep menu on screen
    const padding = 20;
    const left = Math.max(padding, Math.min(window.innerWidth - size - padding, screenX - size / 2));
    const top = Math.max(padding, Math.min(window.innerHeight - size - padding, screenY - size / 2));

    this.root.style.left = `${left}px`;
    this.root.style.top = `${top}px`;

    // Store actual center for angle calculations
    this.centerX = left + size / 2;
    this.centerY = top + size / 2;

    this.root.classList.add('visible');
    this.draw();
  }

  /**
   * Hide the radial menu
   */
  hide(): void {
    this.isVisible = false;
    this.root.classList.remove('visible');
    this.emit('close', undefined);
  }

  /**
   * Update highlight based on pointer position
   * Returns the highlighted index or -1 if in center deadzone
   */
  updatePointer(screenX: number, screenY: number): number {
    if (!this.isVisible) return -1;

    const dx = screenX - this.centerX;
    const dy = screenY - this.centerY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Check if in center deadzone (cancel area)
    const centerEl = this.root.querySelector('.radial-menu-center') as HTMLElement;
    if (distance < this.centerDeadzone) {
      this.highlightedIndex = -1;
      centerEl?.classList.add('hover');
    } else {
      centerEl?.classList.remove('hover');

      // Calculate angle and find segment
      let angle = Math.atan2(dy, dx);
      // Rotate so first segment is at top
      angle = angle + Math.PI / 2;
      if (angle < 0) angle += Math.PI * 2;

      const segmentAngle = (Math.PI * 2) / this.denominations.length;
      this.highlightedIndex = Math.floor(angle / segmentAngle) % this.denominations.length;
    }

    this.draw();
    return this.highlightedIndex;
  }

  /**
   * Select the currently highlighted denomination
   * Returns true if a selection was made, false if cancelled
   */
  select(): boolean {
    if (!this.isVisible) return false;

    if (this.highlightedIndex >= 0) {
      const denomination = this.denominations[this.highlightedIndex];
      this.emit('select', { denomination, index: this.highlightedIndex });
      this.hide();
      return true;
    } else {
      // In center deadzone - cancel
      this.emit('cancel', undefined);
      this.hide();
      return false;
    }
  }

  /**
   * Draw the radial menu
   */
  private draw(): void {
    const ctx = this.ctx;
    const size = this.radius * 2 + 60;
    const center = size / 2;

    // Clear canvas
    ctx.clearRect(0, 0, size, size);

    const numSegments = this.denominations.length;
    const segmentAngle = (Math.PI * 2) / numSegments;
    const innerRadius = this.centerDeadzone + 10;
    const outerRadius = this.radius;

    // Draw segments
    for (let i = 0; i < numSegments; i++) {
      // Start angle (rotated so first segment is at top)
      const startAngle = i * segmentAngle - Math.PI / 2;
      const endAngle = startAngle + segmentAngle;

      // Determine segment state
      const isHighlighted = i === this.highlightedIndex;
      const isCurrent = i === this.currentDenominationIndex;

      // Draw segment background
      ctx.beginPath();
      ctx.moveTo(center + Math.cos(startAngle) * innerRadius, center + Math.sin(startAngle) * innerRadius);
      ctx.arc(center, center, outerRadius, startAngle, endAngle);
      ctx.lineTo(center + Math.cos(endAngle) * innerRadius, center + Math.sin(endAngle) * innerRadius);
      ctx.arc(center, center, innerRadius, endAngle, startAngle, true);
      ctx.closePath();

      // Fill based on state — fully opaque, no transparency
      if (isHighlighted) {
        ctx.fillStyle = this.colors.segmentHighlight;
      } else if (isCurrent) {
        ctx.fillStyle = this.colors.segmentSelected;
      } else {
        ctx.fillStyle = this.colors.segment;
      }
      ctx.fill();

      // Draw border — dim gold default, bright gold when hovered, mid gold for current
      if (isHighlighted) {
        ctx.strokeStyle = this.colors.borderHighlight;
        ctx.lineWidth = 2.5;
      } else if (isCurrent) {
        ctx.strokeStyle = this.colors.borderSelected;
        ctx.lineWidth = 2;
      } else {
        ctx.strokeStyle = this.colors.border;
        ctx.lineWidth = 1.5;
      }
      ctx.stroke();

      // Draw denomination label
      const labelAngle = startAngle + segmentAngle / 2;
      const labelRadius = (innerRadius + outerRadius) / 2;
      const labelX = center + Math.cos(labelAngle) * labelRadius;
      const labelY = center + Math.sin(labelAngle) * labelRadius;

      ctx.save();
      ctx.translate(labelX, labelY);

      // Rotate text to be readable
      let textAngle = labelAngle + Math.PI / 2;
      if (textAngle > Math.PI / 2 && textAngle < Math.PI * 1.5) {
        textAngle += Math.PI;
      }
      ctx.rotate(textAngle);

      // Text style — larger for readability
      ctx.font = isHighlighted ? 'bold 22px "Bebas Neue", sans-serif' : '20px "Bebas Neue", sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      if (isHighlighted) {
        ctx.fillStyle = this.colors.textHighlight;
      } else if (isCurrent) {
        ctx.fillStyle = this.colors.textSelected;
      } else {
        ctx.fillStyle = this.colors.text;
      }

      ctx.fillText(formatDenomination(this.denominations[i]), 0, 0);
      ctx.restore();
    }

    // Draw center circle background
    ctx.beginPath();
    ctx.arc(center, center, this.centerDeadzone, 0, Math.PI * 2);
    ctx.fillStyle = this.colors.background;
    ctx.fill();
  }

  /**
   * Check if menu is visible
   */
  getIsVisible(): boolean {
    return this.isVisible;
  }

  /**
   * Get currently highlighted denomination
   */
  getHighlightedDenomination(): number | null {
    if (this.highlightedIndex < 0) return null;
    return this.denominations[this.highlightedIndex];
  }

  /**
   * Dispose of resources
   */
  dispose(): void {
    this.root.remove();
    this.removeAllListeners();
  }
}
